<script setup>
import { onMounted, watch, ref } from 'vue'
import { useAppStore } from './stores/app'
import { useDataStore } from './stores/data'
import { useMapStore } from './stores/map'
import { eventBus } from '@/utils/eventBus'
import { log } from '@/utils/logger'
import { getQueryStringParameter } from '@/utils/query'
import MapView from './components/maps/MapView.vue'
import EditTripDialog from './components/shared/EditTripDialog.vue'
import BottomSheet from './components/shared/BottomSheet.vue'
import IntroPage from './components/pages/IntroPage.vue'
import TripsPage from './components/pages/TripsPage.vue'
import ApprovePage from './components/pages/ApprovePage.vue'
import ApprovedPage from './components/pages/ApprovedPage.vue'
import OnboardingPage from './components/pages/OnboardingPage.vue'
import PermissionsPage from './components/pages/PermissionsPage.vue'
import SurveyPage from './components/pages/SurveyPage.vue'
import EditTripPage from './components/pages/EditTripPage.vue'
import SplashView from './components/shared/SplashView.vue'
import HelperView from './components/shared/HelperView.vue'
import PromptModal from './components/shared/PromptModal.vue'
import ProfileView from './components/shared/ProfileView.vue'
import DeveloperView from './components/shared/DeveloperView.vue'
import LoginDialog from './components/shared/LoginDialog.vue'
import ProgressDialog from './components/shared/ProgressDialog.vue'

const appStore = useAppStore()
const dataStore = useDataStore()
const mapStore = useMapStore()
const bottomSheetRef = ref(null)

const reload = () => {
	setTimeout(() => {
		document.location.reload()
	}, 200)
}

watch(
	() => appStore.initializing,
	(initializing) => {
		if (initializing) {
			appStore.showSplash()
		}
	},
)

watch(
	() => appStore.invalidPermissions,
	() => {
		console.warn('Invalid permissions changed', appStore.invalidPermissions, appStore.currentPage, appStore.onboardedSurvey)
		if (appStore.currentPage == 'onboarding' && !appStore.invalidPermissions && appStore.onboardedSurvey) {
			appStore.setPage('intro')
		}
		// Make sure onboardedPermissions is false when invalidPermissions is true
		if (appStore.invalidPermissions) {
			appStore.onboardedPermissions = false
		}
	},
)

watch(
	() => dataStore.selectedDate,
	(newValue, oldValue) => {
		console.warn('Selected date changed:', '' + oldValue, ' -> ', newValue, newValue.toShortDateString())
	},
	{ deep: true },
)

// Handle if currentJourney is suddenly null and active page needs a valid journey
watch(
	() => dataStore.currentJourney,
	() => {
		if (appStore.currentPage == 'trips' && dataStore.currentJourney == null) {
			appStore.bottomSheet.setHeightHidden(300, 'App.vue watch currentJourney', false)
			setTimeout(() => {
				appStore.setPage('intro')
				mapStore.plotTrips()
			}, 300)
		}
	},
)

// watch dataStore.respondentAltId async
watch(
	() => dataStore.respondentAltId,
	async (newValue, oldValue) => {
		console.warn('Respondent Alt ID changed:', newValue, 'oldValue:', oldValue)
		if (newValue && !dataStore.respondentShortId) {
			await dataStore.initRespondent()
		}
	},
)

onMounted(async () => {
	// Check for altid URL parameter for testing (doesn't persist with kettle.set)
	const altidParam = getQueryStringParameter('altid')
	if (altidParam) {
		console.warn('App.vue: altid URL parameter detected for testing:', altidParam)
		await dataStore.initRespondent(altidParam)
	}

	if (!dataStore.isApp) {
		appStore.initializing = true
	}
	setLoad('App.vue:onMounted', 3)
	console.warn('onMounted', appStore, dataStore)
	window.parent.postMessage('mounted', '*')
	console.warn('postMessage onMounted done')

	if (dataStore.platform == 'web') {
		const manifestLink = document.createElement('link')
		manifestLink.rel = 'manifest'
		manifestLink.href = '/manifest.json'
		document.head.appendChild(manifestLink)
	}

	await appStore.init()

	// Set up event listeners
	window.addEventListener('message', appStore.messageHandler)

	// Add platform class to document
	document.documentElement.classList.add(dataStore.platform)

	// Store last exit of the app
	window.addEventListener('pause', function (event) {
		log('Paused Event').green()
		const pauseDate = new Date()
		dataStore.pauseDate = pauseDate
		localStorage.setItem('pauseDate', pauseDate.toISOString())
	})

	// Listen for app events
	window.addEventListener('resume', async function (event) {
		log('Resumed Event').green()

		// Update saved states
		await dataStore.loadSavedStates()

		// Always recheck saved states on resume
		await appStore.loadSavedStates()

		// Always recheck permissions on resume
		await appStore.getPermissions()

		// Always recheck consents on resume
		await appStore.getConsents()

		// If permissions are invalid send user to the permissions page
		if (appStore.invalidPermissions) {
			if (appStore.trackingTimeout) appStore.setPage('onboarding')
			else appStore.setPage('permissions')
			return
		}

		// Check time passed since last pause
		if (dataStore.pauseDate) {
			const resumeDate = new Date()
			const pausedSeconds = (resumeDate - dataStore.pauseDate) / 1000 // in seconds
			console.warn('App resumed after', pausedSeconds, 'seconds')
			// If less than 90 minutes since last pause, do not reinitialize
			if (pausedSeconds < 90 * 60) {
				return
			} else {
				// If more than 90 minutes, check if selected date is yesterday
				if (dataStore.platform != 'web' && dataStore.yesterDate instanceof Date && dataStore.selectedDate instanceof Date && dataStore.yesterDate?.toShortDateString() != dataStore.selectedDate?.toShortDateString()) {
					console.warn('App resumed but needs reloading since selected date is not yesterday')
					setTimeout(() => {
						document.location.reload()
					}, 200)
					return
				}
			}
		}

		if (appStore.currentPage == 'permissions') {
			console.error('Silent resume for permissions page')
			return
		}
		if (document.location.search.includes('silent')) {
			console.error('Silent resume')
			return
		}
		if (dataStore.platform != 'web') {
			console.error('--- APP RESUME ---', event)
			if (!dataStore.hasData) appStore.initApp('resume')
		} else {
			console.error('--- APP RESUME ---', 'Ignored for Web', event)
			//if (!dataStore.hasData)
			appStore.initApp('resume')
		}

		// Check if the map is visible
		if (!mapStore.isMapVisible()) {
			// If the map is not visible, reinitialize the map
			mapStore.refresh()
		}
	})

	window.addEventListener('deeplink', (event) => {
		const url = event.detail.url
		console.log('Received deeplink:', url)
		dataStore.handleDeeplink(url)
	})

	appStore.setBottomSheetRef(bottomSheetRef)

	// Load saved states when mounted
	appStore.loadSavedStates()

	// Load saved survey completed async
	kettle.get('survey_completed').then((value) => {
		appStore.surveyCompleted = value ? true : false
	})

	// Handle hat to do when the app is started or resumed
	appStore.initApp('mount')

	// Logging the app start event
	appStore.events.trackAppStart()

	// For debugging
	window.eventBus = eventBus
	window.appStore = appStore
	window.dataStore = dataStore
	window.mapStore = mapStore
})
</script>

<template>
	<div :class="[{ loading: appStore.loading, loaded: appStore.loaded, initializing: appStore.initializing, updating: appStore.updating, preview: appStore.isPreview, frame: appStore.isFrame, debug: appStore.debug }, dataStore.platform]">
		<SplashView /><!-- v-if="dataStore.isPreview || dataStore.reloading"  -->
		<!-- v-if="!appStore.loaded || appStore.initializing" -->

		<PromptModal></PromptModal>

		<ProgressDialog :current-process="dataStore.currentProcess" :visible="!appStore.initializing && ((dataStore.processing && appStore.onboardedSurvey) || (!appStore.onboardedSurvey && dataStore.loadingSurvey))" />

		<BottomSheet ref="bottomSheetRef">
			<OnboardingPage v-if="appStore.trackingTimeout || !appStore.onboardedPermissions || !appStore.onboardedSurvey || dataStore.processingError" />
			<PermissionsPage />
			<SurveyPage v-if="!appStore.onboardedSurvey || !appStore.surveyCompleted || !dataStore.respondentId || !dataStore.respondentShortId" />
			<IntroPage />
			<TripsPage v-if="dataStore.currentJourney" />
			<EditTripPage />
			<ApprovePage v-if="dataStore.currentJourney" />
			<ApprovedPage />
		</BottomSheet>

		<EditTripDialog :show="dataStore.showEditTripDialog" @close="dataStore.closeEditTripDialogClick" />

		<LoginDialog :show="dataStore.showLoginDialog" @close="dataStore.closeLoginDialogClick" />

		<ProfileView />
		<DeveloperView v-if="dataStore.userCanAccessDeveloperOptions" />
		<HelperView />

		<MapView />
	</div>
</template>
